import { useEffect, useState } from 'react';
import { adminApi } from '../services/adminApi';

/**
 * Whether the signed-in user may open /admin. The server is the only judge
 * (ADMIN_EMAILS behind /admin/analytics/health — a 403 means no), so this
 * asks it ONCE per signed-in user and shares the answer: one in-flight
 * request per uid, never a request per mounted component. A failure is a
 * "no" for this session (fail closed), not a retry loop.
 */
const cache = new Map<string, Promise<boolean>>();

export function isAdminOnce(uid: string): Promise<boolean> {
  let p = cache.get(uid);
  if (!p) {
    p = adminApi.checkAdminAccess().then(() => true, () => false);
    cache.set(uid, p);
  }
  return p;
}

/** Test seam / sign-out: forget every cached answer. */
export function resetIsAdminCache(): void {
  cache.clear();
}

export function useIsAdmin(uid: string | null | undefined): boolean {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (!uid) {
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    isAdminOnce(uid).then((v) => { if (!cancelled) setIsAdmin(v); });
    return () => { cancelled = true; };
  }, [uid]);
  return isAdmin;
}
